import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useWarbandStore } from '../../stores/warbandStore';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { manageWarbandService } from '../../services/manageWarbandService';

const createWizardSchema = z.object({
  name: z.string().min(1, 'Wizard name is required'),
  weaponIds: z.array(z.string()),
  armourIds: z.array(z.string()),
});

type CreateWizardFormData = z.infer<typeof createWizardSchema>;

export const CreateWizardModal: React.FC = () => {
  const {
    ui,
    selectedWarbandId,
    closeCreateWizardModal,
    fetchManageWarbandData,
  } = useWarbandStore();

  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const weapons = useReferenceDataStore((s) => s.weapons);

  const { control, handleSubmit, reset } = useForm<CreateWizardFormData>({
    resolver: zodResolver(createWizardSchema),
    defaultValues: {
      name: '',
      weaponIds: [],
      armourIds: [],
    },
  });

  const onSubmit = async (data: CreateWizardFormData) => {
    if (!selectedWarbandId) {
      setError('No party selected');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await manageWarbandService.createWizard(selectedWarbandId, {
        name: data.name,
        weapons: data.weaponIds.map((id) => ({ id })),
        armours: data.armourIds.map((id) => ({ id })),
      });

      await fetchManageWarbandData(selectedWarbandId);
      reset();
      closeCreateWizardModal();
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create wizard';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    closeCreateWizardModal();
  };

  return (
    <Dialog
      open={ui.createWizardModalOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
        {error && (
          <Box sx={{ color: '#d32f2f', fontSize: '0.875rem', p: 1, backgroundColor: '#ffebee', borderRadius: 1 }}>
            {error}
          </Box>
        )}

        <Controller
          name="name"
          control={control}
          render={({ field, fieldState: { error: fieldError } }) => (
            <TextField
              {...field}
              label="Wizard Name"
              fullWidth
              error={!!fieldError}
              helperText={fieldError?.message}
              disabled={loading}
            />
          )}
        />

        <Controller
          name="weaponIds"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth disabled={loading}>
              <InputLabel id="create-wizard-weapons-label">Weapons</InputLabel>
              <Select
                {...field}
                labelId="create-wizard-weapons-label"
                label="Weapons"
                multiple
                renderValue={(selected) => {
                  const selectedIds = selected as string[];
                  return weapons
                    .filter((weapon) => selectedIds.includes(weapon.id))
                    .map((weapon) => weapon.name)
                    .join(', ');
                }}
              >
                {weapons.map((weapon) => (
                  <MenuItem key={weapon.id} value={weapon.id}>
                    <Checkbox checked={field.value.includes(weapon.id)} />
                    <ListItemText primary={weapon.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Create Wizard'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
